import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  EnvConfig,
  ILogger,
  MediaStatus,
  MediaType,
  useAuthentication,
  useGrippContext,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useState } from 'react';
import { Linking, Platform, Pressable, Text, View } from 'react-native';
import { Card, ProgressBar } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { XIcon } from '../crop-circle/icons';
import { colors } from '../crop-circle/tokens';
import { getMediaTitleForWidth } from './mediaUtils';
import { ProcessingPlaceholder } from './processingPlaceholder';
import { DocumentData, MediaComponent, MediaComponentProps } from './types';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

const isLocalOrFileUrl = (url: string) =>
  url.startsWith('blob:') || url.startsWith('file:');

const isApiDocumentUrl = (url: string) =>
  url.includes('/document/') &&
  (url.startsWith(EnvConfig.apiUrl) || url.startsWith('/'));

const getDocumentApiUrl = (url: string): string => {
  if (!url || url.startsWith('http')) return url;
  const base = EnvConfig.apiUrl?.replace(/\/$/, '') || '';
  return base ? `${base}${url.startsWith('/') ? url : `/${url}`}` : url;
};

// app + web, will need to separate the web and app components if the functionality diverges further
export const DocumentComponent: MediaComponent<DocumentData> = ({
  displayMode = 'card',
  onPress,
  canDelete,
  ...props
}: MediaComponentProps<DocumentData>) => {
  const { logger } = useGrippContext();
  const authentication = useAuthentication();
  const documentCardStyle =
    displayMode === 'card'
      ? styles.documentThumbCard
      : styles.documentOutlinedCard;
  const defaultWidth = 88;
  const defaultHeight = 88;
  const displayWidth = props.displayWidth ?? defaultWidth;
  const displayHeight =
    props.displayWidth && props.displayWidth > defaultHeight
      ? props.displayWidth
      : defaultHeight;
  const fileIconSize = displayWidth / 4;
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  const isPdf = props.mediaItem.extension?.toLowerCase() === 'pdf';
  const canRenderPdfThumbnail =
    Platform.OS === 'web' &&
    props.mediaItem.type === MediaType.Document &&
    isPdf;

  useEffect(() => {
    let previewObjectUrl: string | null = null;
    let isCancelled = false;

    const loadPdfPreview = async () => {
      if (!canRenderPdfThumbnail || !props.mediaItem.url) {
        setPdfPreviewUrl(null);
        return;
      }

      if (isLocalOrFileUrl(props.mediaItem.url)) {
        setPdfPreviewUrl(props.mediaItem.url);
        return;
      }

      if (!isApiDocumentUrl(props.mediaItem.url)) {
        setPdfPreviewUrl(props.mediaItem.url);
        return;
      }

      if (authentication?.isLoading) {
        return;
      }

      const finalUrl = getDocumentApiUrl(props.mediaItem.url);
      if (!finalUrl.startsWith('http')) {
        setPdfPreviewUrl(null);
        return;
      }

      try {
        const headers: HeadersInit = {};
        if (authentication?.accessToken) {
          headers['Authorization'] = `Bearer ${authentication.accessToken}`;
        }

        const response = await fetch(finalUrl, {
          method: 'GET',
          headers: { ...headers, Accept: 'application/json' },
          credentials: 'omit',
        });

        if (!response.ok) {
          throw new Error('Failed to resolve PDF thumbnail URL');
        }

        const data = (await response.json()) as { downloadUrl?: string };
        if (!data.downloadUrl) {
          throw new Error('Missing PDF download URL');
        }

        const fileResponse = await fetch(data.downloadUrl);
        if (!fileResponse.ok) {
          throw new Error('Failed to load PDF blob');
        }

        const fileBlob = await fileResponse.blob();
        if (typeof URL?.createObjectURL === 'function') {
          if (isCancelled) {
            return;
          }
          const objectUrl = URL.createObjectURL(fileBlob);
          if (isCancelled && typeof URL?.revokeObjectURL === 'function') {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          previewObjectUrl = objectUrl;
        }

        if (!isCancelled) {
          setPdfPreviewUrl(previewObjectUrl);
        }
      } catch (error) {
        if (!isCancelled) {
          setPdfPreviewUrl(null);
        }
        logger?.error(
          error,
          `Failed to generate PDF thumbnail for ${props.mediaItem.id}`
        );
      }
    };

    void loadPdfPreview();

    return () => {
      isCancelled = true;
      if (previewObjectUrl && typeof URL?.revokeObjectURL === 'function') {
        URL.revokeObjectURL(previewObjectUrl);
      }
    };
  }, [
    authentication?.accessToken,
    authentication?.isLoading,
    canRenderPdfThumbnail,
    logger,
    props.mediaItem.id,
    props.mediaItem.url,
  ]);

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress();
      return;
    }
    downloadDocument(props.mediaItem.status, props.mediaItem.url, logger);
  }, [props.mediaItem.status, props.mediaItem.url, logger, onPress]);

  const previewContent =
    canRenderPdfThumbnail && pdfPreviewUrl ? (
      <View style={styles.pdfPreviewContainer} pointerEvents="none">
        <iframe
          src={pdfPreviewUrl}
          title={props.mediaItem.name || 'PDF preview'}
          style={styles.pdfFrame}
        />
      </View>
    ) : (
      <>
        <MaterialCommunityIcons
          style={styles.icon}
          name={props.mediaItem.type === MediaType.Document ? 'file' : 'video'}
          color={Colors.primaryDark}
          size={fileIconSize}
        />
        <Text style={styles.documentTitle}>
          {getMediaTitleForWidth(
            props.mediaItem.name,
            props.mediaItem.extension,
            displayWidth
          )}
        </Text>
      </>
    );

  return (
    <ProcessingPlaceholder
      processing={props.mediaItem.showProcessing ?? false}
      width={displayWidth}
      height={displayHeight}
      displayMode={displayMode}
      url={props.mediaItem.url}
      name={props.mediaItem.name}
      extension={props.mediaItem.extension}
    >
      <Card
        onPress={handlePress}
        mode={displayMode === 'outlined' ? 'outlined' : 'elevated'}
        style={[
          styles.baseCardCropCircle,
          { width: displayWidth, height: displayHeight },
        ]}
      >
        {canDelete && (
          <Pressable
            style={styles.closeButton}
            onPress={() => props.deleteMediaItem?.(props.mediaItem.id)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={
              props.mediaItem.type === MediaType.Document
                ? 'Remove document'
                : 'Remove video'
            }
          >
            <XIcon size={8} color={Colors.role.textStrong} />
          </Pressable>
        )}
        <Card.Content
          style={[
            styles.cardContent,
            { width: displayWidth, height: displayHeight },
          ]}
        >
          {previewContent}
        </Card.Content>
        {props.mediaItem.isUploading && (
          <ProgressBar animatedValue={props.mediaItem.uploadProgress} />
        )}
      </Card>
    </ProcessingPlaceholder>
  );
};

const downloadDocument = async (
  mediaStatus: MediaStatus,
  url: string,
  logger?: ILogger
) => {
  if (isLocalUrl(url)) {
    await openUrl(url);
    return;
  }
  fetch(url, { method: 'GET' })
    .then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        await openUrl(data.downloadUrl);
      }
    })
    .catch((e) => {
      logger?.error(e, `Failed to download document from ${url}`);
    });
};

const openUrl = async (url: string) => {
  const supported = await Linking.canOpenURL(url);
  if (supported) {
    await Linking.openURL(url);
  }
};

const isLocalUrl = (url: string) => {
  return url.startsWith('blob:') || url.startsWith('file://');
};

const styles = themedStyleSheet((role) => ({
  icon: {
    alignSelf: 'center',
  },
  baseCardCropCircle: {
    borderRadius: 4,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: role.surfaceRaised,
    cursor: 'pointer',
  },
  cardContent: {
    paddingTop: 0,
    paddingHorizontal: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  documentThumbCard: {},
  documentOutlinedCard: {
    borderWidth: 0.5,
    borderColor: role.borderStrong,
  },
  documentTitle: {
    textAlign: 'center',
    fontWeight: '500',
    fontSize: 12,
    width: '100%',
    color: role.textStrong,
  },
  pdfPreviewContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 4,
  },
  pdfFrame: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 99,
    backgroundColor: role.surfaceInverseSoftHover,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
}));
